import './fonts.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import './mantineOverrides.css';
import React from 'react';
import Root from './routes/Root';
import ApplicationRoot from './routes/ApplicationRoot';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { withFaroRouterInstrumentation } from '@grafana/faro-react';

import { AppSettingsProvider } from '@hud/web-components/theme/settings';
import ExploreRoot from './routes/Explore/ExploreRoot';
import ServicesPage from './routes/Explore/ServicesPage';
import ServicePage from './routes/Explore/ServicePage';
import FunctionsPage from './routes/Explore/FunctionsPage/FunctionsPage';
import FunctionPage from './routes/Explore/FunctionPage/FunctionPage';
import SettingsRoot from './routes/Settings/SettingsRoot';
import MembersPage from './routes/Settings/MembersPage/MembersPage';
import EndpointsPage from './routes/Explore/EndpointsPage';
import EndpointPage from './routes/Explore/EndpointPage';
import UnregisteredPage from '@hud/web-components/ErrorPages/UnregisteredPage/UnregisteredPage';
import GeneralErrorPage from '@hud/web-components/ErrorPages/GeneralErrorPage/GeneralErrorPage';
import SSORequired from '@hud/web-components/ErrorPages/SSORequired/SSORequired';
import MessageQueuesPage from './routes/Explore/MessageQueuesPage/MessageQueuesPage';
import MessageQueuePage from './routes/Explore/MessageQueuePage/MessageQueuePage';
import LiveCode from './routes/LiveCode/LiveCodeRoot';
import PRComparisonsPage from './routes/Explore/PRComparisonsPage/PRComparisonsPage';
import JobPage from './routes/Explore/JobPage/JobPage';
import HeadsUpRoot from './routes/HeadsUp/HeadsUpRoot';
import HeadsUpRulesPage from './routes/HeadsUp/HeadsUpRulesPage/HeadsUpRulesPage';
import HeadsUpRulePage from './routes/HeadsUp/HeadsUpRulePage/HeadsUpRulePage';
import HeadsUpSettingsPage from './routes/HeadsUp/HeadsUpSettingsPage/HeadsUpSettingsPage';
import APIKeysPage from './routes/Settings/APIKeysPage/APIKeysPage';
import { HeadsUpIssueRedirect } from './routes/HeadsUp/HeadsUpRoot';
import SetupPage from './routes/Setup/SetupPage';
import StandaloneAPIKeysPage from './routes/StandaloneApiKeys/StandaloneAPIKeysPage';
import OverviewPage from './routes/Overview/OverviewPage';
import SlackIntegrationPage from './routes/Settings/SlackIntegrationPage/SlackIntegrationPage';
import IssuesPage from './routes/HeadsUp/HeadsUpIssuesPage/IssuesPage';
import HeadsUpIssuePage from './routes/HeadsUp/HeadsUpIssuePage/HeadsUpIssuePage';

export default function App() {
  const router = React.useMemo(() => {
    const baseRouter = createBrowserRouter([
      {
        path: '/unauthorized',
        element: <UnregisteredPage />,
      },
      {
        path: '/sso-required',
        element: <SSORequired />,
      },
      {
        path: '/login-failed',
        element: <GeneralErrorPage />,
      },
      {
        path: '/setup',
        element: <SetupPage />,
      },
      {
        path: '/api-keys',
        element: <StandaloneAPIKeysPage />,
      },
      {
        path: '/',
        element: <Root />,
        children: [
          {
            element: <ApplicationRoot />,
            children: [
              {
                path: 'explore',
                element: <ExploreRoot />,
                children: [
                  {
                    path: 'services/:id',
                    element: <ServicePage />,
                  },
                  {
                    path: 'services',
                    element: <ServicesPage />,
                  },
                  {
                    path: 'functions',
                    element: <FunctionsPage />,
                  },
                  {
                    path: 'functions/:id',
                    element: <FunctionPage />,
                  },
                  {
                    path: 'endpoints',
                    element: <EndpointsPage />,
                  },
                  {
                    path: 'endpoints/:endpointAndService',
                    element: <EndpointPage />,
                  },
                  {
                    path: 'prs',
                    element: <PRComparisonsPage />,
                  },
                  {
                    path: 'prs/:jobId',
                    element: <JobPage />,
                  },
                  {
                    path: 'messageQueues',
                    element: <MessageQueuesPage />,
                  },
                  {
                    path: 'messageQueues/:flowId',
                    element: <MessageQueuePage />,
                  },
                ],
              },
              {
                path: 'overview',
                element: <OverviewPage />,
              },
              {
                path: 'live-code',
                element: <LiveCode />,
              },
              {
                path: 'heads-up',
                element: <HeadsUpRoot />,
                children: [
                  {
                    path: 'issues',
                    element: <IssuesPage />,
                  },
                  {
                    path: 'issues/:issueId',
                    element: <HeadsUpIssuePage />,
                  },
                  {
                    path: 'rules',
                    element: <HeadsUpRulesPage />,
                  },
                  {
                    path: 'rules/:ruleId',
                    element: <HeadsUpRulePage />,
                  },
                  {
                    path: ':issueId',
                    element: <HeadsUpIssueRedirect />,
                  },
                ],
              },
            ],
          },
          {
            path: 'settings',
            element: <SettingsRoot />,
            children: [
              {
                path: 'members',
                element: <MembersPage />,
              },
              {
                path: 'heads-up',
                element: <HeadsUpSettingsPage />,
              },
              {
                path: 'api-keys',
                element: <APIKeysPage />,
              },
              {
                path: 'slack',
                element: <SlackIntegrationPage />,
              },
              // Default settings page
              {
                path: '',
                element: <Navigate to={'/settings/members'} replace />,
              },
            ],
          },
        ],
      },
    ]);

    return withFaroRouterInstrumentation(baseRouter);
  }, []);

  return (
    <AppSettingsProvider>
      <RouterProvider router={router} />
    </AppSettingsProvider>
  );
}
